import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * useConsultantDashboard
 *
 * Encapsulates all data fetching, session management, and form logic
 * for the consultant dashboard. The React component focuses on JSX only.
 */
export const useConsultantDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({
    active_sessions: 0,
    completed_today: 0,
    assigned_users: 0,
    scheduled: 0,
    completed_engagements: 0,
    total_this_week: 0,
    pending_requests: 0
  });
  const [activeTab, setActiveTab] = useState('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Completed sessions view
  const [completedUsers, setCompletedUsers] = useState([]);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userAssessments, setUserAssessments] = useState({});
  const [loadingAssessments, setLoadingAssessments] = useState({});
  const [userAssessmentCounts, setUserAssessmentCounts] = useState({}); // Cache for consultant-led counts

  // Assigned users tab
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [userActiveSessions, setUserActiveSessions] = useState({});

  // Session modal states
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAssessmentType, setSelectedAssessmentType] = useState('ESA');
  const [startingSession, setStartingSession] = useState(false);
  const [openingModal, setOpeningModal] = useState(false);
  const [openingModalUserId, setOpeningModalUserId] = useState(null);

  // Industry and company data states
  const [majorIndustries, setMajorIndustries] = useState([]); // Array of industry objects
  const [subIndustriesMap, setSubIndustriesMap] = useState({});
  const [availableSubIndustries, setAvailableSubIndustries] = useState([]);
  const [selectedMajorIndustry, setSelectedMajorIndustry] = useState(''); // Industry name
  const [selectedMajorIndustryObj, setSelectedMajorIndustryObj] = useState(null); // Full industry object with region
  const [selectedSubIndustry, setSelectedSubIndustry] = useState('');
  const [industriesLoading, setIndustriesLoading] = useState(false);

  // Company information states
  const [companyName, setCompanyName] = useState('');
  const [annualEnergySpend, setAnnualEnergySpend] = useState('');
  const [facilitySize, setFacilitySize] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [state, setState] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');

  // Log completed users whenever they change (preserves original behavior)
  useEffect(() => {
    logger.log(completedUsers, 'completed users data');
  }, [completedUsers]);

  const loadRealData = useCallback(async () => {
    try {
      logger.log('📊 Loading consultant dashboard data (optimized)...');
      const startTime = performance.now();
      setLoading(true);
      setError('');

      const response = await fetch(`${API_URL}/api/consultant/dashboard-data`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch consultant data');
      }

      const data = await response.json();
      const endTime = performance.now();
      logger.log(
        `✅ Dashboard data loaded in ${(
          (endTime - startTime) / 1000
        ).toFixed(2)}s`
      );
      
      // Debug: Log first session structure to understand API response
      if (data.sessions && data.sessions.length > 0) {
        const sampleSession = data.sessions[0];
        logger.log('📋 Sample session structure from API:', {
          id: sampleSession.id,
          industry: sampleSession.industry,
          industry_id: sampleSession.industry_id,
          industry_name: sampleSession.industry_name,
          sub_industry_name: sampleSession.sub_industry_name,
          major_industry: sampleSession.major_industry,
          user: sampleSession.user,
          client: sampleSession.client,
          status: sampleSession.status,
          completed_at: sampleSession.completed_at
        });
      }

      // Map sessions
      const mappedSessions = (data.sessions || []).map((session) => {
        const client = session.user || session.client || {};
        const industry = session.industry || {};
        
        // Extract industry name from various possible structures
        // Backend returns: { 'industry': { 'id': ..., 'name': ..., 'region': ... } }
        // Backend now always returns a valid industry object (never null)
        let industryName = 'Not specified';
        
        // Priority 1: Check if industry is an object with name property (backend format)
        if (industry && typeof industry === 'object' && industry !== null && !Array.isArray(industry)) {
          industryName = industry.name || industry.description || 'Not specified';
        } 
        // Priority 2: Check if industry is a string
        else if (typeof industry === 'string' && industry.trim()) {
          industryName = industry.trim();
        }
        
        // If still not found, check session fields directly (these are from live_sessions table)
        if (industryName === 'Not specified') {
          // Priority 3: Check session.industry_name (direct field from live_sessions)
          if (session.industry_name && typeof session.industry_name === 'string' && session.industry_name.trim()) {
            industryName = session.industry_name.trim();
          }
          // Priority 4: Check sub_industry_name
          else if (session.sub_industry_name && typeof session.sub_industry_name === 'string' && session.sub_industry_name.trim()) {
            industryName = session.sub_industry_name.trim();
          }
          // Priority 5: Check major_industry
          else if (session.major_industry && typeof session.major_industry === 'string' && session.major_industry.trim()) {
            industryName = session.major_industry.trim();
          }
        }
        
        // Only log warning if we truly couldn't find any industry data (should be rare now)
        if (industryName === 'Not specified' && session.id) {
          // Use debug level instead of warn to reduce console noise
          logger.debug('Could not extract industry name for session:', {
            sessionId: session.id,
            industry: industry,
            industry_id: session.industry_id,
            industry_name: session.industry_name,
            sub_industry_name: session.sub_industry_name,
            major_industry: session.major_industry
          });
        }

        return {
          id: session.id,
          assessmentId: session.assessment_id,
          status: session.status,
          clientName: client.name || 'Unknown',
          clientEmail: client.email || '',
          clientCompany: client.company || '',
          type: session.assessment_type || 'ESA',
          industry: industryName,
          startTime: session.created_at,
          completedTime: session.completed_at || session.ended_at || session.completion_date || null,
          scheduledTime: session.scheduled_at || session.created_at,
          assessmentType: session.assessment_type || 'ESA',
          facilityName: '',
          totalSavings: 0,
          completedQuestions: 0,
          totalQuestions: 0,
          // Mark as consultant-led since all sessions from consultant dashboard are consultant-led
          isConsultantLed: true,
          source: session.source || 'live_session'
        };
      });
      
      logger.log(`📊 Mapped ${mappedSessions.length} sessions`);

      setSessions(mappedSessions);

      // Stats from backend
      setStats(
        data.stats || {
          active_sessions: 0,
          completed_today: 0,
          assigned_users: 0,
          scheduled: 0,
          completed_engagements: 0,
          total_this_week: 0,
          pending_requests: 0
        }
      );

      // Build completed users - all sessions from consultant dashboard are consultant-led
      // We'll fetch the actual consultant-led assessments when user expands to get accurate count
      const allCompletedSessions = mappedSessions.filter((s) => s.status === 'completed');
      logger.log(`📋 Found ${allCompletedSessions.length} completed sessions (total mapped: ${mappedSessions.length})`);
      
      const completedSessionsMap = new Map();
      allCompletedSessions
        .filter((s) => s.clientEmail) // Only include sessions with valid email
        .forEach((session) => {
          const userKey = session.clientEmail;
          if (!completedSessionsMap.has(userKey)) {
            completedSessionsMap.set(userKey, {
              email: session.clientEmail,
              name: session.clientName,
              completedCount: 0,
              lastCompleted: null
            });
          }
          const userData = completedSessionsMap.get(userKey);
          // Count all completed sessions (they're all consultant-led from this endpoint)
          userData.completedCount += 1;
          // Update lastCompleted with the most recent completed time
          if (session.completedTime) {
            try {
              const completedDate = new Date(session.completedTime);
              if (!isNaN(completedDate.getTime())) {
                if (!userData.lastCompleted || completedDate > new Date(userData.lastCompleted)) {
                  userData.lastCompleted = session.completedTime;
                }
              } else {
                logger.warn('⚠️ Invalid completedTime for session:', session.id, session.completedTime);
              }
            } catch (e) {
              logger.error('Error parsing completed time:', e, session.completedTime);
            }
          } else {
            logger.warn('⚠️ Missing completedTime for session:', session.id);
          }
        });

      const completedUsersList = Array.from(completedSessionsMap.values());
      logger.log(`✅ Built ${completedUsersList.length} completed users:`, completedUsersList.map(u => ({
        email: u.email,
        name: u.name,
        count: u.completedCount,
        lastCompleted: u.lastCompleted
      })));
      setCompletedUsers(completedUsersList);
      
      // Fetch consultant-led assessment counts for all completed users in the background
      // Use setTimeout to defer this non-critical operation and not block initial render
      setTimeout(() => {
        fetchConsultantLedCountsForAllUsers(completedUsersList);
      }, 100); // Small delay to let UI render first

      // Assigned users from backend
      setAssignedUsers(data.assigned_users || []);

      // Build active sessions map
      const sessionsMap = {};
      (data.assigned_users || []).forEach((u) => {
        if (u.active_session) {
          sessionsMap[u.id] = u.active_session;
        }
      });
      setUserActiveSessions(sessionsMap);
    } catch (err) {
      logger.error('❌ Error loading consultant data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial auth + data load
  useEffect(() => {
    if (!user) {
      logger.log('❌ No user in AuthContext');
      navigate('/consultant/login');
      return;
    }

    logger.log('✅ Consultant authenticated:', user);
    // Force reload data - clear any cached state first to ensure fresh data
    setCompletedUsers([]);
    setSessions([]);
    setStats({
      active_sessions: 0,
      completed_today: 0,
      assigned_users: 0,
      scheduled: 0,
      completed_engagements: 0,
      total_this_week: 0,
      pending_requests: 0
    });
    loadRealData();
  }, [user, navigate, loadRealData]);

  // Poll for pending requests updates (real-time)
  useEffect(() => {
    if (!user) return;

    let isMounted = true;

    // Poll every 5 seconds for pending requests
    const pollInterval = setInterval(async () => {
      if (!isMounted) return;

      try {
        const response = await fetch(`${API_URL}/api/consultant/dashboard-data`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });

        if (!response.ok || !isMounted) return;

        const data = await response.json();
        const newPendingRequests = data.stats?.pending_requests || 0;
        
        // Use functional update to get current state
        setStats(prevStats => {
          // Only update if pending requests count changed
          if (newPendingRequests !== prevStats.pending_requests) {
            logger.log(`🔄 Pending requests updated: ${prevStats.pending_requests} → ${newPendingRequests}`);
            
            // If new pending requests appeared, also refresh sessions to show them
            if (newPendingRequests > prevStats.pending_requests && isMounted) {
              logger.log('🔄 New pending request detected, refreshing sessions...');
              // Light refresh - reload sessions in background
              fetch(`${API_URL}/api/consultant/dashboard-data`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include'
              })
                .then(res => res.ok ? res.json() : null)
                .then(sessionsData => {
                  if (!sessionsData || !isMounted) return;
                  
                  // Map and update sessions
                  const mappedSessions = (sessionsData.sessions || []).map((session) => {
                    const client = session.user || session.client || {};
                    const industry = session.industry || {};
                    
                    let industryName = 'Not specified';
                    if (industry && typeof industry === 'object' && industry !== null && !Array.isArray(industry)) {
                      industryName = industry.name || industry.description || 'Not specified';
                    } else if (typeof industry === 'string' && industry.trim()) {
                      industryName = industry.trim();
                    }
                    
                    if (industryName === 'Not specified') {
                      if (session.industry_name && typeof session.industry_name === 'string' && session.industry_name.trim()) {
                        industryName = session.industry_name.trim();
                      } else if (session.sub_industry_name && typeof session.sub_industry_name === 'string' && session.sub_industry_name.trim()) {
                        industryName = session.sub_industry_name.trim();
                      } else if (session.major_industry && typeof session.major_industry === 'string' && session.major_industry.trim()) {
                        industryName = session.major_industry.trim();
                      }
                    }

                    return {
                      id: session.id,
                      assessmentId: session.assessment_id,
                      status: session.status,
                      clientName: client.name || 'Unknown',
                      clientEmail: client.email || '',
                      clientCompany: client.company || '',
                      type: session.assessment_type || 'ESA',
                      industry: industryName,
                      startTime: session.created_at,
                      completedTime: session.completed_at || session.ended_at || session.completion_date || null,
                      scheduledTime: session.scheduled_time,
                      isConsultantLed: true,
                      source: 'live_session'
                    };
                  });
                  
                  if (isMounted) {
                    setSessions(mappedSessions);
                    
                    // Update assigned users if available
                    if (sessionsData.assigned_users) {
                      setAssignedUsers(sessionsData.assigned_users);
                    }
                  }
                })
                .catch(err => {
                  logger.debug('Error refreshing sessions (non-critical):', err);
                });
            }
            
            return {
              ...prevStats,
              pending_requests: newPendingRequests
            };
          }
          return prevStats;
        });
      } catch (error) {
        // Silently fail polling - don't spam console
        logger.debug('Polling error (non-critical):', error);
      }
    }, 5000); // Poll every 5 seconds

    return () => {
      isMounted = false;
      clearInterval(pollInterval);
    };
  }, [user]); // Only depend on user, not stats to avoid re-creating interval

  // Fetch consultant-led assessment counts for all completed users (background, non-blocking)
  // Uses batched requests to avoid overwhelming the server
  const fetchConsultantLedCountsForAllUsers = useCallback(async (usersList) => {
    if (!usersList || usersList.length === 0) return;
    
    logger.log(`📊 Fetching consultant-led counts for ${usersList.length} users (batched)...`);
    
    // Process in batches of 5 to avoid overwhelming the server
    const BATCH_SIZE = 5;
    const batches = [];
    for (let i = 0; i < usersList.length; i += BATCH_SIZE) {
      batches.push(usersList.slice(i, i + BATCH_SIZE));
    }
    
    const countsMap = {};
    
    // Process batches sequentially to avoid too many parallel requests
    for (const batch of batches) {
      const batchPromises = batch.map(async (userData) => {
        try {
          const response = await fetch(
            `${API_URL}/api/consultant/user-assessments-by-email?email=${encodeURIComponent(
              userData.email
            )}&consultant_led_only=true`,
            {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include'
            }
          );

          if (!response.ok) {
            logger.debug(`Failed to fetch counts for ${userData.email}`);
            return null;
          }

          const data = await response.json();
          // When consultant_led_only=true, all returned assessments are consultant-led
          const consultantLedAssessments = data.assessments || [];
          const consultantLedCount = consultantLedAssessments.length;
          
          // Get the most recent completion date
          const lastCompletedDate = consultantLedAssessments
            .map(a => a.completion_date)
            .filter(d => d)
            .map(d => new Date(d))
            .filter(d => !isNaN(d.getTime()))
            .sort((a, b) => b - a)?.[0];
          
          return {
            email: userData.email,
            count: consultantLedCount,
            lastCompleted: lastCompletedDate ? lastCompletedDate.toISOString() : null
          };
        } catch (err) {
          logger.debug(`Error fetching counts for ${userData.email}:`, err);
          return null;
        }
      });
      
      // Wait for batch to complete
      const batchCounts = await Promise.all(batchPromises);
      
      // Update counts map
      batchCounts.forEach(count => {
        if (count) {
          countsMap[count.email] = {
            count: count.count,
            lastCompleted: count.lastCompleted
          };
        }
      });
      
      // Update UI incrementally as batches complete
      setUserAssessmentCounts(prev => ({ ...prev, ...countsMap }));
      setCompletedUsers(prevUsers => 
        prevUsers.map(user => {
          const countData = countsMap[user.email];
          if (countData) {
            return {
              ...user,
              completedCount: countData.count,
              lastCompleted: countData.lastCompleted || user.lastCompleted
            };
          }
          return user;
        })
      );
    }
    
    logger.log(`✅ Fetched consultant-led counts for ${Object.keys(countsMap).length} users`);
  }, []);

  const fetchUserAssessments = useCallback(async (userEmail) => {
    try {
      setLoadingAssessments((prev) => ({ ...prev, [userEmail]: true }));

      const response = await fetch(
        `${API_URL}/api/consultant/user-assessments-by-email?email=${encodeURIComponent(
          userEmail
        )}&consultant_led_only=true`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch user assessments');
      }

      const data = await response.json();
      setUserAssessments((prev) => ({ ...prev, [userEmail]: data }));
      
      // Update the count cache when assessments are fetched
      // When consultant_led_only=true, all returned assessments are consultant-led
      const consultantLedAssessments = data.assessments || [];
      const consultantLedCount = consultantLedAssessments.length;
      const lastCompletedDate = consultantLedAssessments
        .map(a => a.completion_date)
        .filter(d => d)
        .map(d => new Date(d))
        .filter(d => !isNaN(d.getTime()))
        .sort((a, b) => b - a)?.[0];
      
      setUserAssessmentCounts(prev => ({
        ...prev,
        [userEmail]: {
          count: consultantLedCount,
          lastCompleted: lastCompletedDate ? lastCompletedDate.toISOString() : null
        }
      }));
      
      // Also update completedUsers state with the accurate count
      setCompletedUsers(prevUsers => 
        prevUsers.map(user => {
          if (user.email === userEmail) {
            return {
              ...user,
              completedCount: consultantLedCount,
              lastCompleted: lastCompletedDate ? lastCompletedDate.toISOString() : user.lastCompleted
            };
          }
          return user;
        })
      );
    } catch (err) {
      logger.error('Error fetching user assessments:', err);
      setUserAssessments((prev) => ({
        ...prev,
        [userEmail]: { error: err.message }
      }));
    } finally {
      setLoadingAssessments((prev) => ({ ...prev, [userEmail]: false }));
    }
  }, []);

  const toggleUserAssessments = useCallback(
    (userEmail) => {
      if (expandedUser === userEmail) {
        setExpandedUser(null);
      } else {
        setExpandedUser(userEmail);
        if (!userAssessments[userEmail]) {
          fetchUserAssessments(userEmail);
        }
      }
    },
    [expandedUser, userAssessments, fetchUserAssessments]
  );

  const fetchIndustries = useCallback(async () => {
    try {
      setIndustriesLoading(true);
      const response = await axios.get(`${API_URL}/api/industries`, {
        withCredentials: true
      });

      const industriesData =
        response.data.industries || response.data.data || response.data;

      if (industriesData && Array.isArray(industriesData) && industriesData.length > 0) {
        // Store full industry objects (not just names) so we can access region
        const validIndustries = industriesData
          .filter(industry => industry.name && industry.name.trim())
          .sort((a, b) => {
            // Sort by region first (NAICS before ANZSIC), then by name
            if (a.region !== b.region) {
              return (a.region || '').localeCompare(b.region || '');
            }
            return (a.name || '').localeCompare(b.name || '');
          });
        
        setMajorIndustries(validIndustries);
        
        // Store industries map for later use (keyed by name)
        const industriesMap = {};
        validIndustries.forEach(industry => {
          if (industry.name) {
            industriesMap[industry.name] = industry;
          }
        });
        
        // Store industries map in state for sub-industry fetching
        setSubIndustriesMap(industriesMap);
        
        logger.log(`Loaded ${validIndustries.length} major industries with regions:`, 
          validIndustries.map(ind => `${ind.name} (${ind.region || 'N/A'})`));
      } else {
        logger.warning('No industries data received from API');
        setMajorIndustries([]);
      }
    } catch (err) {
      logger.error('Error fetching industries:', err);
      toast.error('Failed to load industries');
      setMajorIndustries([]);
    } finally {
      setIndustriesLoading(false);
    }
  }, []);

  // Pre-fetch industries on component mount to avoid delay when opening modal
  useEffect(() => {
    fetchIndustries();
  }, [fetchIndustries]);

  const handleMajorIndustryChange = useCallback(
    async (e) => {
      const selectedValue = e.target.value;
      const selectedOption = e.target.options[e.target.selectedIndex];
      const region = selectedOption.getAttribute('data-region') || 'naics';
      const majorIndustryName = selectedOption.getAttribute('data-name') || '';
      const industryId = selectedOption.getAttribute('data-id') || '';
      
      setSelectedMajorIndustry(majorIndustryName);
      setSelectedSubIndustry('');
      setAvailableSubIndustries([]);

      // Find the full industry object - match by ID first (most reliable), then by name+region
      const selectedIndustryObj = majorIndustries.find(
        ind => {
          if (typeof ind === 'string') {
            return ind === majorIndustryName;
          }
          // Match by ID if available (most reliable)
          if (industryId && ind.id === industryId) {
            return true;
          }
          // Fallback: match by name and region
          return ind.name === majorIndustryName && ind.region === region;
        }
      );
      
      if (!selectedIndustryObj) {
        logger.warning(`Could not find industry object for: ${majorIndustryName} (${region}), ID: ${industryId}`);
      }
      
      setSelectedMajorIndustryObj(selectedIndustryObj || null);
      
      const resolvedRegion = selectedIndustryObj && typeof selectedIndustryObj !== 'string' 
        ? (selectedIndustryObj.region || region)
        : region;
      
      logger.log(`Selected industry: ${majorIndustryName}, Region: ${resolvedRegion}, ID: ${selectedIndustryObj?.id || 'N/A'}`);

      if (majorIndustryName) {
        try {
          // Fetch sub-industries for the selected major industry with correct region
          const response = await axios.get(
            `${API_URL}/api/sub-industries-by-major`,
            {
              params: {
                major_industry: majorIndustryName,
                region: resolvedRegion
              },
              withCredentials: true
            }
          );

          const subIndustriesData = response.data.sub_industries || response.data.data || [];
          
          if (Array.isArray(subIndustriesData) && subIndustriesData.length > 0) {
            const subIndustryNames = subIndustriesData
              .map(sub => sub.name || sub.sub_industry_name)
              .filter(name => name && name.trim())
              .sort();
            
            setAvailableSubIndustries(subIndustryNames);
            logger.log(`Loaded ${subIndustryNames.length} sub-industries for ${majorIndustryName} (${resolvedRegion})`);
          } else {
            logger.warning(`No sub-industries found for major industry: ${majorIndustryName} (${resolvedRegion})`);
            setAvailableSubIndustries([]);
          }
        } catch (err) {
          logger.error('Error fetching sub-industries:', err);
          toast.error('Failed to load sub-industries');
          setAvailableSubIndustries([]);
        }
      }
    },
    [majorIndustries]
  );

  const handleSubIndustryChange = useCallback((e) => {
    setSelectedSubIndustry(e.target.value);
  }, []);

  const handleStartSessionClick = useCallback(
    async (userInfo) => {
      setOpeningModal(true);
      setOpeningModalUserId(userInfo.id);
      
      try {
        setSelectedUser(userInfo);

        // Check for pending request/session and set assessment type
        const hasPending = userInfo.has_pending_request || userInfo.has_pending_session;
        logger.log('🔍 User info when opening modal:', {
          has_pending_request: userInfo.has_pending_request,
          has_pending_session: userInfo.has_pending_session,
          pending_session: userInfo.pending_session,
          assessment_type: userInfo.pending_session?.assessment_type
        });
        
        if (hasPending && userInfo.pending_session && userInfo.pending_session.assessment_type) {
          const requestedType = userInfo.pending_session.assessment_type.toUpperCase();
          logger.log(`🔒 Setting assessment type from pending request: ${requestedType}`);
          setSelectedAssessmentType(requestedType);
        } else {
          logger.log('⚠️ No pending request found, defaulting to ESA');
          setSelectedAssessmentType('ESA');
        }

        setSelectedMajorIndustry('');
        setSelectedSubIndustry('');
        setSelectedMajorIndustryObj(null);
        setAvailableSubIndustries([]);
      setCompanyName(userInfo.company || '');
      setAnnualEnergySpend('');
      setFacilitySize('');
      setContactName(userInfo.name || '');
      setContactEmail(userInfo.email || '');
      setState('');
      setSessionNotes('');

        // Industries should already be pre-loaded, but ensure they're ready
        // Don't block modal opening - industries are already fetched on mount
        if (majorIndustries.length === 0 && !industriesLoading) {
          // Fetch in background, don't wait
          await fetchIndustries();
        }

        try {
          const response = await axios.get(
            `${API_URL}/api/live-session/user/${userInfo.id}/active`,
            { withCredentials: true }
          );

          if (response.data.has_active_session) {
            const session = response.data.session;
            if (session.status === 'active') {
              toast.error(
                `${userInfo.name} already has an active ${session.assessment_type} session. Please complete or cancel it first.`,
                { duration: 5000 }
              );
              return;
            }
          }
        } catch (err) {
          logger.error('Error checking active session:', err);
        }

        setShowSessionModal(true);
      } finally {
        setOpeningModal(false);
        setOpeningModalUserId(null);
      }
    },
    [fetchIndustries, majorIndustries, industriesLoading]
  );

  const handleStartSession = useCallback(async () => {
    if (!selectedUser) return;

    // Import validation utility
    const { validateFacilityInfo } = await import('../../utils/assessment/facilityValidation');

    // Validate all required facility fields
    const facilityInfo = {
      companyName: companyName?.trim() || '',
      contactName: contactName?.trim() || '',
      email: contactEmail?.trim() || '',
      state: state?.trim() || '',
      majorIndustry: selectedMajorIndustry || '',
      subIndustry: selectedSubIndustry || ''
    };

    const validation = validateFacilityInfo(facilityInfo, true); // true = consultant-guided

    if (!validation.isValid) {
      // Show first error
      toast.error(validation.errors[0]);
      return;
    }

    try {
      setStartingSession(true);

      const config = {
        withCredentials: true
      };

      // Get industry_id from selected major industry object
      const industryId = selectedMajorIndustryObj && typeof selectedMajorIndustryObj !== 'string'
        ? selectedMajorIndustryObj.id
        : null;
      
      const region = selectedMajorIndustryObj && typeof selectedMajorIndustryObj !== 'string'
        ? (selectedMajorIndustryObj.region || 'naics')
        : 'naics';

      const payload = {
        user_id: selectedUser.id,
        assessment_type: selectedAssessmentType,
        industry_id: industryId, // Pass industry_id to avoid ambiguity
        industry_name: selectedMajorIndustry, // Major industry name
        major_industry: selectedMajorIndustry,
        sub_industry_name: selectedSubIndustry, // Sub-industry name
        region: region, // Pass region explicitly
        company_name: (companyName || '').trim(),
        annual_energy_spend: annualEnergySpend
          ? parseFloat(annualEnergySpend)
          : null,
        facility_size: (facilitySize || '').trim() || null,
        contact_name: (contactName || '').trim(),
        contact_email: (contactEmail || '').trim(),
        state: (state || '').trim(),
        notes: (sessionNotes || '').trim() || null
      };

      const response = await axios.post(
        `${API_URL}/api/live-session/start`,
        payload,
        config
      );

      if (response.data.success) {
        toast.success('Live session started successfully!');
        setShowSessionModal(false);
        setSelectedUser(null);

        const sessionId = response.data.session.id;
        navigate(`/consultant/session/${sessionId}`);
      }
    } catch (err) {
      logger.error('Error starting session:', err);

      if (err.response?.status === 409) {
        const errorMessage =
          err.response?.data?.detail || 'User already has an active session';
        toast.error(errorMessage, { duration: 6000 });
      } else {
        toast.error(err.response?.data?.detail || 'Failed to start session');
      }
    } finally {
      setStartingSession(false);
    }
  }, [
    selectedUser,
    selectedAssessmentType,
    selectedMajorIndustry,
    selectedSubIndustry,
    companyName,
    annualEnergySpend,
    facilitySize,
    contactName,
    contactEmail,
    state,
    sessionNotes,
    selectedMajorIndustryObj,
    navigate
  ]);

  const handleStartNewSession = useCallback(() => {
    navigate('/assessment/self-guided?consultant_mode=true');
  }, [navigate]);

  const handleResumeSession = useCallback(
    (sessionId) => {
      navigate(`/consultant/session/${sessionId}`);
    },
    [navigate]
  );

  const handleViewResults = useCallback(
    (session) => {
      logger.log(session, 'sessions------');
      if (session.assessmentId) {
        navigate(`/assessment/results/${session.assessmentId}`);
      } else {
        toast.error('Assessment results not available for this session');
      }
    },
    [navigate]
  );

  const getStatusBadge = useCallback((status) => {
    const badges = {
      in_progress:
        'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
      completed:
        'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
      scheduled:
        'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
      paused:
        'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
    };

    const labels = {
      in_progress: 'In Progress',
      completed: 'Completed',
      scheduled: 'Scheduled',
      paused: 'Paused'
    };

    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${badges[status]}`}
      >
        {labels[status]}
      </span>
    );
  }, []);

  const formatTime = useCallback((isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  return {
    user,
    sessions,
    stats,
    activeTab,
    setActiveTab,
    loading,
    error,
    mobileMenuOpen,
    setMobileMenuOpen,
    completedUsers,
    expandedUser,
    userAssessments,
    loadingAssessments,
    assignedUsers,
    userActiveSessions,
    showSessionModal,
    setShowSessionModal,
    selectedUser,
    selectedAssessmentType,
    setSelectedAssessmentType,
    startingSession,
    openingModal,
    openingModalUserId,
    majorIndustries,
    availableSubIndustries,
    selectedMajorIndustry,
    selectedMajorIndustryObj,
    selectedSubIndustry,
    industriesLoading,
    companyName,
    setCompanyName,
    annualEnergySpend,
    setAnnualEnergySpend,
    facilitySize,
    setFacilitySize,
    contactName,
    setContactName,
    contactEmail,
    setContactEmail,
    state,
    setState,
    sessionNotes,
    setSessionNotes,
    loadRealData,
    handleStartNewSession,
    handleResumeSession,
    handleViewResults,
    toggleUserAssessments,
    handleStartSessionClick,
    handleStartSession,
    handleMajorIndustryChange,
    handleSubIndustryChange,
    getStatusBadge,
    formatTime
  };
};


